import React, { useState, useEffect } from 'react';
import { Horse, Contract } from './types';
import { loadHorsesData } from './horsesData';
import { saveHorseList, saveContract, fetchContractHistory } from './api';
import './HorseEditor.css';

// Get base path for API calls (app is at domain root)
const getBasePath = () => {
  return '/databaseOps/';
};

const HorseEditor: React.FC = () => {
  const [horses, setHorses] = useState<Horse[]>([]);
  const [editingHorse, setEditingHorse] = useState<Horse | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [nextId, setNextId] = useState(40);
  const [searchTerm, setSearchTerm] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(true);
  const [endContractDate, setEndContractDate] = useState<string>('');
  const [contractHistory, setContractHistory] = useState<Contract[]>([]);
  const [showContractHistory, setShowContractHistory] = useState(false);
  const [showOwnerChange, setShowOwnerChange] = useState(false);
  const [ownerChangeData, setOwnerChangeData] = useState({
    change_date: new Date().toISOString().split('T')[0],
    owner_name: '',
    owner_street: '',
    owner_city: '',
    owner_phone: '',
    box_rent: 0,
  });

  // Load horses on component mount
  useEffect(() => {
    loadHorsesData()
      .then(({ horses, nextId }) => {
        setHorses(horses);
        setNextId(nextId);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load horses:', error);
        alert('Fehler beim Laden der Pferde-Daten');
        setIsLoading(false);
      });
  }, []);

  const getEmptyHorse = (): Horse => ({
    id: `pony/${nextId}`,
    name: '',
    boxNumber: '',
    owner: '',
    phone: '',
    weight: 0,
    hidden: false,
    early: 'nichts',
    late: 'nichts',
    bricks: 'keine',
    contractDate: new Date().toISOString().split('T')[0]
  });

  const handleAdd = () => {
    setEditingHorse(getEmptyHorse());
    setIsAdding(true);
  };

  const handleEdit = (horse: Horse) => {
    setEditingHorse({ ...horse });
    setIsAdding(false);
    setContractHistory([]);
    setShowContractHistory(false);

    // Load contract history in background
    fetchContractHistory(horse.id)
      .then(contracts => setContractHistory(contracts))
      .catch(err => console.error('Failed to load contract history:', err));
  };

  // handleDelete removed — visibility is managed through contract lifecycle

  const saveToServer = async (updatedHorses: Horse[]): Promise<void> => {
    setSaveStatus('saving');
    try {
      const data = {
        ponyNextID: nextId,
        ponys: updatedHorses.reduce((acc, horse) => {
          acc[horse.id] = horse;
          return acc;
        }, {} as Record<string, Horse>)
      };

      await saveHorseList(data);

      console.log('Save successful');
      setSaveStatus('saved');
      // Wait 2 seconds to show success message
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveStatus('idle');
    } catch (error: any) {
      console.error('Error saving:', error);
      setSaveStatus('error');
      alert(`Fehler beim Speichern:\n\n${error.message}\n\nDetails siehe Browser-Konsole (F12)`);
      // Wait 3 seconds to show error message
      await new Promise(resolve => setTimeout(resolve, 3000));
      setSaveStatus('idle');
      throw error; // Re-throw so handleSave knows it failed
    }
  };

  const handleSave = async () => {
    if (!editingHorse) return;

    if (!editingHorse.name.trim()) {
      alert('Bitte geben Sie einen Namen ein.');
      return;
    }

    let updatedHorses: Horse[];
    if (isAdding) {
      updatedHorses = [...horses, editingHorse];
      setHorses(updatedHorses);
      setNextId(nextId + 1);
    } else {
      updatedHorses = horses.map(h => h.id === editingHorse.id ? editingHorse : h);
      setHorses(updatedHorses);
    }

    // Save to server and wait for completion (including status display delay)
    try {
      await saveToServer(updatedHorses);
      // Only close modal after successful save and delay
      setEditingHorse(null);
      setIsAdding(false);
    } catch (error) {
      // Keep modal open on error so user can retry
      console.error('Save failed, keeping modal open');
    }
  };

  const handleCancel = () => {
    setEditingHorse(null);
    setIsAdding(false);
    setEndContractDate('');
    setContractHistory([]);
    setShowContractHistory(false);
  };

  const handleEndContract = async () => {
    if (!editingHorse?.contract) return;

    const endDate = endContractDate || new Date().toISOString().split('T')[0];

    if (!window.confirm(
      `Möchten Sie den Vertrag wirklich beenden?\n\n` +
      `Pferd: ${editingHorse.name}\n` +
      `Besitzer: ${editingHorse.contract.owner_name}\n` +
      `Enddatum: ${new Date(endDate).toLocaleDateString('de-DE')}\n\n` +
      `Das Pferd wird nach diesem Datum automatisch ausgeblendet.`
    )) {
      return;
    }

    setSaveStatus('saving');

    try {
      const updatedContract = {
        ...editingHorse.contract,
        end_date: endDate,
        indefinite: false,
        hidden: false  // Don't hide immediately — auto-hide logic handles it after end-of-month
      };

      const result = await saveContract(updatedContract);

      if (!result.success) {
        throw new Error(result.error || 'Failed to end contract');
      }

      console.log('Contract ended successfully:', result);
      setSaveStatus('saved');

      // Reload horse data to get updated contract info
      const { horses: updatedHorses, nextId: newNextId } = await loadHorsesData();
      setHorses(updatedHorses);
      setNextId(newNextId);

      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveStatus('idle');
      setEditingHorse(null);
      setEndContractDate('');

      alert(`Vertrag für "${editingHorse.name}" wurde beendet.`);
    } catch (error: any) {
      console.error('Error ending contract:', error);
      setSaveStatus('error');
      alert(`Fehler beim Beenden des Vertrags:\n\n${error.message}\n\nDetails siehe Browser-Konsole (F12)`);
      await new Promise(resolve => setTimeout(resolve, 3000));
      setSaveStatus('idle');
    }
  };

  const handleOwnerChange = async () => {
    if (!editingHorse?.contract) return;

    if (!ownerChangeData.owner_name.trim()) {
      alert('Bitte geben Sie den Namen des neuen Besitzers ein.');
      return;
    }

    if (!window.confirm(
      `Besitzerwechsel durchführen?\n\n` +
      `Pferd: ${editingHorse.name}\n` +
      `Alter Besitzer: ${editingHorse.contract.owner_name}\n` +
      `Neuer Besitzer: ${ownerChangeData.owner_name}\n` +
      `Wechseldatum: ${new Date(ownerChangeData.change_date).toLocaleDateString('de-DE')}\n\n` +
      `Der alte Vertrag wird beendet und ein neuer Vertrag angelegt.`
    )) {
      return;
    }

    setSaveStatus('saving');

    try {
      const response = await fetch(`${getBasePath()}changeOwner.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          horse_id: editingHorse.id,
          change_date: ownerChangeData.change_date,
          new_owner: {
            owner_name: ownerChangeData.owner_name,
            owner_street: ownerChangeData.owner_street,
            owner_city: ownerChangeData.owner_city,
            owner_phone: ownerChangeData.owner_phone,
            box_rent: ownerChangeData.box_rent,
          }
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Owner change failed');
      }

      setSaveStatus('saved');

      const { horses: updatedHorses, nextId: newNextId } = await loadHorsesData();
      setHorses(updatedHorses);
      setNextId(newNextId);

      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveStatus('idle');
      setEditingHorse(null);
      setShowOwnerChange(false);

      alert(`Besitzerwechsel für "${editingHorse.name}" erfolgreich durchgeführt.`);
    } catch (error: any) {
      console.error('Error changing owner:', error);
      setSaveStatus('error');
      alert(`Fehler beim Besitzerwechsel:\n\n${error.message}`);
      await new Promise(resolve => setTimeout(resolve, 3000));
      setSaveStatus('idle');
    }
  };

  const handleSetHorseStatus = async (action: 'deceased' | 'absent' | 'return') => {
    if (!editingHorse) return;

    const callApi = async (body: object) => {
      setSaveStatus('saving');
      const response = await fetch(`${getBasePath()}setHorseStatus.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Status update failed');
      }
      return result;
    };

    try {
      if (action === 'deceased') {
        const dateStr = prompt('Todesdatum (JJJJ-MM-TT):', new Date().toISOString().split('T')[0]);
        if (dateStr === null) return;

        if (!window.confirm(
          `"${editingHorse.name}" als verstorben markieren?\n\n` +
          `Datum: ${new Date(dateStr).toLocaleDateString('de-DE')}\n\n` +
          `Der aktive Vertrag wird beendet. Das Pferd wird nach dem Kulanzmonat ausgeblendet.`
        )) return;

        await callApi({ horse_id: editingHorse.id, action: 'deceased', deceased_date: dateStr });
        alert(`"${editingHorse.name}" wurde als verstorben markiert.`);

      } else if (action === 'absent') {
        const reason = prompt('Grund der Abwesenheit (z.B. Klinik, Turnier):');
        if (reason === null) return;
        const until = prompt('Voraussichtliche Rückkehr (JJJJ-MM-TT, leer = unbekannt):');
        if (until === null) return;

        await callApi({
          horse_id: editingHorse.id,
          action: 'absent',
          reason: reason,
          until: until || null
        });
        alert(`"${editingHorse.name}" wurde als abwesend markiert.`);

      } else if (action === 'return') {
        if (!window.confirm(`"${editingHorse.name}" ist zurück?`)) return;
        await callApi({ horse_id: editingHorse.id, action: 'return' });
        alert(`"${editingHorse.name}" ist zurückgekehrt.`);
      }

      setSaveStatus('saved');
      const { horses: updatedHorses, nextId: newNextId } = await loadHorsesData();
      setHorses(updatedHorses);
      setNextId(newNextId);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveStatus('idle');
      setEditingHorse(null);

    } catch (error: any) {
      console.error('Error updating horse status:', error);
      setSaveStatus('error');
      alert(`Fehler: ${error.message}`);
      await new Promise(resolve => setTimeout(resolve, 3000));
      setSaveStatus('idle');
    }
  };

  const handleInputChange = (field: keyof Horse, value: any) => {
    if (editingHorse) {
      setEditingHorse({
        ...editingHorse,
        [field]: value
      });
    }
  };

  // Get display status for a contract/horse
  const getContractStatus = (horse: Horse): { label: string; className: string } => {
    if (horse.deceased) {
      return { label: 'Verstorben', className: 'status-deceased' };
    }
    if (horse.hidden) {
      return { label: 'Beendet', className: 'status-ended' };
    }
    if (horse.absent) {
      return {
        label: `Abwesend${horse.absent_reason ? ` (${horse.absent_reason})` : ''}`,
        className: 'status-absent'
      };
    }
    if (horse.contract && !horse.contract.indefinite && horse.contract.end_date) {
      const endDate = new Date(horse.contract.end_date);
      return {
        label: `Endet ${endDate.toLocaleDateString('de-DE')}`,
        className: 'status-ending'
      };
    }
    return { label: 'Aktiv', className: 'status-active' };
  };

  // Determine if a horse should be visible based on hidden status and contract end date
  const isHorseVisible = (horse: Horse): boolean => {
    // If not hidden, always visible
    if (!horse.hidden) return true;

    // If showHidden toggle is on, show everything
    if (showHidden) return true;

    // If hidden, check if contract end date is in current month or future
    if (horse.contract?.end_date) {
      const endDate = new Date(horse.contract.end_date);
      const today = new Date();

      // Get year-month for comparison (year * 12 + month gives comparable values)
      const endYearMonth = endDate.getFullYear() * 12 + endDate.getMonth();
      const currentYearMonth = today.getFullYear() * 12 + today.getMonth();

      // Visible if contract ends in current month or later
      return endYearMonth >= currentYearMonth;
    }

    // If hidden and no end date, hide it
    return false;
  };

  const filteredHorses = horses
    .filter(horse => isHorseVisible(horse))
    .filter(horse =>
      horse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      horse.owner.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  if (isLoading) {
    return (
      <div className="horse-editor">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Lade Pferde-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="horse-editor">
      <header className="header">
        <h2>Pferde</h2>
        <h4>
          <strong>{filteredHorses.length}</strong> {filteredHorses.length === 1 ? 'Pferd' : 'Pferde'}
          {!showHidden && horses.filter(h => h.hidden).length > 0 && (
            <span className="hidden-count"> | {horses.filter(h => h.hidden).length} ausgeblendet</span>
          )}
        </h4>
      </header>

      <div className="editor-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Suche nach Name oder Besitzer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="control-buttons">
          <label>
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
            />
            Ehemalige anzeigen
          </label>
        </div>
      </div>

      {editingHorse && (
        <>
          <div className="modal-overlay" onClick={handleCancel}></div>
          <div className="modal-dialog">
            <h3>{isAdding ? 'Neues Pferd hinzufügen' : 'Pferd bearbeiten'}</h3>
            {saveStatus !== 'idle' && (
              <div className={`save-status-modal save-status-${saveStatus}`}>
                {saveStatus === 'saving' && '💾 Speichert...'}
                {saveStatus === 'saved' && '✓ Gespeichert!'}
                {saveStatus === 'error' && '⚠ Fehler beim Speichern'}
              </div>
            )}
            <div className="form-grid">
            <div className="form-field">
              <label>Name *</label>
              <input
                type="text"
                value={editingHorse.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Box-Nummer</label>
              <input
                type="text"
                value={editingHorse.boxNumber || ''}
                onChange={(e) => handleInputChange('boxNumber', e.target.value)}
                placeholder="z.B. A1, B3, OS1"
              />
            </div>
            <div className="form-field">
              <label>Besitzer</label>
              <input
                type="text"
                value={editingHorse.owner}
                onChange={(e) => handleInputChange('owner', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Telefon</label>
              <input
                type="text"
                value={editingHorse.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Gewicht (kg)</label>
              <input
                type="number"
                value={editingHorse.weight}
                onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="form-field">
              <label>Morgens</label>
              <input
                type="text"
                value={editingHorse.early || ''}
                onChange={(e) => handleInputChange('early', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Abends</label>
              <input
                type="text"
                value={editingHorse.late || ''}
                onChange={(e) => handleInputChange('late', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Mineralbricks</label>
              <input
                type="text"
                value={editingHorse.bricks || ''}
                onChange={(e) => handleInputChange('bricks', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Vertragsdatum</label>
              <input
                type="date"
                value={editingHorse.contractDate || ''}
                onChange={(e) => handleInputChange('contractDate', e.target.value)}
              />
            </div>
            </div>

            {/* Contract Information Section */}
            {editingHorse.contract && (
              <div className="contract-info-section">
                <h4>Vertragsinformationen</h4>
                <div className="contract-info-grid">
                  <div className="contract-field">
                    <label>Besitzer:</label>
                    <span>{editingHorse.contract.owner_name}</span>
                  </div>
                  {editingHorse.contract.owner_street && (
                    <div className="contract-field">
                      <label>Straße:</label>
                      <span>{editingHorse.contract.owner_street}</span>
                    </div>
                  )}
                  {editingHorse.contract.owner_city && (
                    <div className="contract-field">
                      <label>Ort:</label>
                      <span>{editingHorse.contract.owner_city}</span>
                    </div>
                  )}
                  <div className="contract-field">
                    <label>Telefon:</label>
                    <span>{editingHorse.contract.owner_phone || '-'}</span>
                  </div>
                  <div className="contract-field">
                    <label>Boxenmiete:</label>
                    <span>{editingHorse.contract.box_rent.toFixed(2)} €</span>
                  </div>
                  {editingHorse.contract.rent_surcharge > 0 && (
                    <div className="contract-field">
                      <label>Aufschlag:</label>
                      <span>{editingHorse.contract.rent_surcharge.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="contract-field">
                    <label>Vertragsdatum:</label>
                    <span>{new Date(editingHorse.contract.contract_date).toLocaleDateString('de-DE')}</span>
                  </div>
                  <div className="contract-field">
                    <label>Mietbeginn:</label>
                    <span>{new Date(editingHorse.contract.start_date).toLocaleDateString('de-DE')}</span>
                  </div>
                  <div className="contract-field">
                    <label>Mietende:</label>
                    <span>
                      {editingHorse.contract.indefinite
                        ? 'Unbefristet'
                        : editingHorse.contract.end_date
                          ? new Date(editingHorse.contract.end_date).toLocaleDateString('de-DE')
                          : '-'
                      }
                    </span>
                  </div>
                  <div className="contract-field">
                    <label>Boxgröße:</label>
                    <span>{editingHorse.contract.large_box ? '3,50m' : '3m'}</span>
                  </div>
                  <div className="contract-field">
                    <label>Status:</label>
                    <span className={getContractStatus(editingHorse).className}>
                      {getContractStatus(editingHorse).label}
                    </span>
                  </div>
                </div>

                {/* End Contract Section - only show if contract is active and not already ended */}
                {!editingHorse.contract.hidden && (editingHorse.contract.indefinite || !editingHorse.contract.end_date) && (
                  <div className="end-contract-section">
                    <h5>Vertrag beenden</h5>
                    <div className="end-contract-controls">
                      <div className="form-field">
                        <label>Enddatum (optional, Standard: heute):</label>
                        <input
                          type="date"
                          value={endContractDate}
                          onChange={(e) => setEndContractDate(e.target.value)}
                          placeholder="Heute"
                        />
                      </div>
                      <button onClick={handleEndContract} className="btn-end-contract">
                        Vertrag beenden
                      </button>
                    </div>
                  </div>
                )}

                {/* Owner Change Section - only show if contract is active */}
                {!editingHorse.contract.hidden && (editingHorse.contract.indefinite || !editingHorse.contract.end_date) && (
                  <div className="owner-change-section">
                    <h5
                      onClick={() => {
                        setShowOwnerChange(!showOwnerChange);
                        if (!showOwnerChange && editingHorse.contract) {
                          setOwnerChangeData(prev => ({
                            ...prev,
                            box_rent: editingHorse.contract!.box_rent,
                          }));
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {showOwnerChange ? '▼' : '▶'} Besitzerwechsel
                    </h5>
                    {showOwnerChange && (
                      <div className="owner-change-form">
                        <div className="form-grid">
                          <div className="form-field">
                            <label>Wechseldatum *</label>
                            <input
                              type="date"
                              value={ownerChangeData.change_date}
                              onChange={(e) => setOwnerChangeData({...ownerChangeData, change_date: e.target.value})}
                            />
                          </div>
                          <div className="form-field">
                            <label>Neuer Besitzer *</label>
                            <input
                              type="text"
                              value={ownerChangeData.owner_name}
                              onChange={(e) => setOwnerChangeData({...ownerChangeData, owner_name: e.target.value})}
                              placeholder="Name des neuen Besitzers"
                            />
                          </div>
                          <div className="form-field">
                            <label>Straße</label>
                            <input
                              type="text"
                              value={ownerChangeData.owner_street}
                              onChange={(e) => setOwnerChangeData({...ownerChangeData, owner_street: e.target.value})}
                            />
                          </div>
                          <div className="form-field">
                            <label>PLZ / Ort</label>
                            <input
                              type="text"
                              value={ownerChangeData.owner_city}
                              onChange={(e) => setOwnerChangeData({...ownerChangeData, owner_city: e.target.value})}
                            />
                          </div>
                          <div className="form-field">
                            <label>Telefon</label>
                            <input
                              type="text"
                              value={ownerChangeData.owner_phone}
                              onChange={(e) => setOwnerChangeData({...ownerChangeData, owner_phone: e.target.value})}
                            />
                          </div>
                          <div className="form-field">
                            <label>Boxenmiete (€)</label>
                            <input
                              type="number"
                              value={ownerChangeData.box_rent}
                              onChange={(e) => setOwnerChangeData({...ownerChangeData, box_rent: parseFloat(e.target.value) || 0})}
                            />
                          </div>
                        </div>
                        <button onClick={handleOwnerChange} className="btn-owner-change">
                          Besitzerwechsel durchführen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Contract History Section */}
            {contractHistory.length > 1 && (
              <div className="contract-history-section">
                <h4
                  onClick={() => setShowContractHistory(!showContractHistory)}
                  style={{ cursor: 'pointer' }}
                >
                  {showContractHistory ? '▼' : '▶'} Vertragshistorie ({contractHistory.length} Vertr{contractHistory.length === 1 ? 'ag' : 'äge'})
                </h4>
                {showContractHistory && (
                  <table className="contract-history-table">
                    <thead>
                      <tr>
                        <th>Besitzer</th>
                        <th>Mietbeginn</th>
                        <th>Mietende</th>
                        <th>Miete</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contractHistory.map(contract => (
                        <tr key={contract.id} className={contract.active ? 'active-contract' : 'past-contract'}>
                          <td>{contract.owner_name}</td>
                          <td>{new Date(contract.start_date).toLocaleDateString('de-DE')}</td>
                          <td>
                            {contract.indefinite
                              ? 'Unbefristet'
                              : contract.end_date
                                ? new Date(contract.end_date).toLocaleDateString('de-DE')
                                : '-'
                            }
                          </td>
                          <td>{(contract.box_rent as number).toFixed(2)} €</td>
                          <td>
                            {contract.active
                              ? (contract.hidden ? 'Beendet' : 'Aktiv')
                              : 'Abgeschlossen'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Horse Status Actions */}
            {!isAdding && !editingHorse.deceased && (
              <div className="horse-status-actions">
                {editingHorse.absent ? (
                  <button onClick={() => handleSetHorseStatus('return')} className="btn-return">
                    Pferd ist zurück
                  </button>
                ) : (
                  <button onClick={() => handleSetHorseStatus('absent')} className="btn-absent">
                    Abwesend melden
                  </button>
                )}
                <button onClick={() => handleSetHorseStatus('deceased')} className="btn-deceased">
                  Verstorben
                </button>
              </div>
            )}
            {editingHorse.deceased && (
              <div className="deceased-notice">
                Verstorben am {editingHorse.deceased_date ? new Date(editingHorse.deceased_date).toLocaleDateString('de-DE') : 'unbekannt'}
              </div>
            )}

            <div className="form-buttons">
              <button onClick={handleSave} className="btn-save">
                Speichern
              </button>
              <button onClick={handleCancel} className="btn-cancel">
                Abbrechen
              </button>
            </div>
          </div>
        </>
      )}

      <div className="horse-grid">
        {filteredHorses.map((horse) => {
          const status = getContractStatus(horse);
          return (
            <div
              key={horse.id}
              className={`horse-card ${horse.hidden ? 'horse-card-hidden' : ''}`}
              onClick={() => handleEdit(horse)}
            >
              <div className={`card-status-bar ${status.className}`}></div>
              <div className="card-body">
                <div className="card-name">{horse.name}</div>
                <div className="card-owner">{horse.owner || 'Kein Besitzer'}</div>
                {horse.boxNumber && (
                  <div className="card-box">Box {horse.boxNumber}</div>
                )}
                {(horse.absent || horse.deceased) && (
                  <div className={`card-badge ${horse.deceased ? 'badge-deceased' : 'badge-absent'}`}>
                    {horse.deceased ? 'Verstorben' : horse.absent_reason || 'Abwesend'}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="horse-card horse-card-add" onClick={handleAdd}>
          <div className="card-body">
            <div className="card-add-icon">+</div>
            <div className="card-add-label">Neues Pferd</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorseEditor;
